import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient, ApolloError } from '@apollo/client';
import { Modal, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    DeleteUserGroupDocument,
    type DeleteUserGroupMutation,
    type DeleteUserGroupMutationVariables,
} from '../../../api/mutations/deleteUserGroup';

const useDeleteUserGroup = (userGroupId: string) => {
    const { t } = useTranslation(['userGroupDetails']);

    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    return useCallback(() => {
        Modal.confirm({
            title: t('userGroupDetails:deleteUserGroup.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('userGroupDetails:deleteUserGroup.message'),
            okText: t('userGroupDetails:deleteUserGroup.okText'),
            okType: 'danger',
            cancelText: t('userGroupDetails:deleteUserGroup.cancelText'),
            onOk: async () => {
                try {
                    // loading message
                    message.loading({
                        content: t('userGroupDetails:deleteUserGroup.messages.submitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteUserGroupMutation, DeleteUserGroupMutationVariables>({
                            mutation: DeleteUserGroupDocument,
                            variables: { userGroupId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('userGroupDetails:deleteUserGroup.messages.success'),
                        key: 'primary',
                    });

                    navigate('/admin/accesses/userGroups');
                } catch (error) {
                    if (error instanceof ApolloError) {
                        message.error(error.graphQLErrors[0].message);
                    }
                }
            },
        });
    }, [t, apolloClient, userGroupId, navigate]);
};

export default useDeleteUserGroup;
